import { Store } from "../../../Application/Store/Store";
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { computeAndDisplayTeethIntersection } from '../../Scene/Handler/CollisionHandler';
import {
    makeVector,
    productScalare,
    projectVector,
    normalizeVector,
    calculateAngleBetweenVectors
} from "../../Scene/Manager/TransformUtils";
import {
    getIdTooth,
    getCentralPitMaloc,
    getCentralPitActual,
    getRotationRootMaloc,
    getRotationRootActual,
    getVecRidgeActual,
    getVecRidgeMaloc,
    getVecRootActual,
    getVecVestibularLingualActual,
    getVecArchTemplateNormal,
    getVecArchTemplateTangent,
    getVecTangentToFacialActual,
} from '../Query/ToothQueryService';

const MOVEMENT_TYPES = [
    'torque',
    'extrusionIntrusion',
    'distalMesial',
    'lingualVestibular',
    'rotation',
    'tip',
];

export const updateAllMovementsDatas = async (tooth) => {
    const mandiGroupGlobal = threeEngine.getMandiGroup();
    if (!mandiGroupGlobal) return;

    const wasAdjustActive = Store.getState().ViewportReducer.mandiAdjustActive;
    const backupPosition = mandiGroupGlobal.position.clone();
    const tempGroup = mandiGroupGlobal.clone(true);
    tempGroup.name = 'mandiGroupTemp';

    try {
        if (wasAdjustActive) {
            const scene = threeEngine.getScene();
            if (scene) scene.add(tempGroup);
            mandiGroupGlobal.visible = false;
            mandiGroupGlobal.position.set(0, 0, 0);
        }

        const toothId = getIdTooth(tooth);

        await Promise.all(
            MOVEMENT_TYPES.map((type, index) =>
                new Promise(resolve => {
                    setTimeout(() => {
                        try {
                            Store.dispatch({
                                type: 'ADD_MOVEMENT',
                                payload: {
                                    toothId,
                                    movementType: type,
                                    value: getMovementValue(tooth, type),
                                },
                            });
                        } catch (err) {
                            console.warn(`Erreur sur movement ${type} :`, err);
                        }
                        resolve();
                    }, index);
                })
            )
        );
    } finally {
        mandiGroupGlobal.position.copy(backupPosition);
    }

    if (wasAdjustActive) {
        const vector = Store.getState().ViewportReducer.mandiAdjust;
        mandiGroupGlobal.position.set(vector.x, vector.y, vector.z);
        const scene = threeEngine.getScene();
        if (scene) scene.remove(tempGroup);
        mandiGroupGlobal.visible = true;
    }

    await computeAndDisplayTeethIntersection();
};

export const getMovementValue = (tooth, movementType) => {
    const toothId = parseInt(getIdTooth(tooth), 10);
    let value = null;
    let angle = null;
    let originToCurrent = null;
    let malocToCurrent = null;
    let refVector = null;
    let projected1 = null;
    let projected2 = null;
    let sign = 1;

    switch (movementType) {
        case 'extrusionIntrusion': {
            if (toothId < 30) sign = -1;
            malocToCurrent = makeVector(getCentralPitMaloc(tooth), getCentralPitActual(tooth));
            value = productScalare([0, 0, sign], malocToCurrent);
            break;
        }
        case 'lingualVestibular': {
            originToCurrent = makeVector(getRotationRootMaloc(tooth), getRotationRootActual(tooth));
            refVector = normalizeVector(projectVector([0, 0, 1], getVecVestibularLingualActual(tooth)));
            value = -productScalare(refVector, originToCurrent);
            break;
        }
        case 'distalMesial': {
            originToCurrent = makeVector(getRotationRootMaloc(tooth), getRotationRootActual(tooth));
            refVector = normalizeVector(projectVector([0, 0, 1], getVecRidgeActual(tooth)));
            value = productScalare(refVector, originToCurrent);
            const isMandiRight = (toothId > 20 && toothId < 29) || (toothId > 40 && toothId < 49);
            if (isMandiRight) value = -value;
            break;
        }
        case 'rotation': {
            refVector = getVecRootActual(tooth);
            projected1 = projectVector(refVector, getVecRidgeMaloc(tooth));
            value = -calculateAngleBetweenVectors(getVecRidgeActual(tooth), projected1, refVector, true);
            break;
        }
        case 'tip': {
            const isMaxi = (toothId >= 11 && toothId <= 18) || (toothId >= 21 && toothId <= 28);
            const isRightMaxi = toothId >= 11 && toothId <= 18;
            const isRightMandi = toothId >= 31 && toothId <= 38;

            sign = isMaxi ? -1 : 1;
            refVector = getVecArchTemplateNormal(tooth);
            projected2 = projectVector(refVector, getVecRootActual(tooth));
            angle = calculateAngleBetweenVectors([0, 0, sign], projected2, refVector, true);

            if (isMaxi && isRightMaxi) angle = -angle;

            sign = angle < 0 ? -1 : 1;
            if (Math.abs(angle) > 90) {
                angle = sign * (180 - Math.abs(angle));
            }
            if (isRightMandi) angle = -angle;
            value = angle ?? 0;
            break;
        }
        case 'torque': {
            if (toothId < 29) sign = -1;
            refVector = getVecArchTemplateTangent(tooth);
            projected2 = projectVector(refVector, normalizeVector(getVecTangentToFacialActual(tooth)));
            angle = calculateAngleBetweenVectors([0, 0, sign], projected2, refVector, true);

            if (toothId < 29) angle = -angle;

            sign = angle < 0 ? -1 : 1;
            if (Math.abs(angle) > 90) {
                angle = sign * (180 - Math.abs(angle));
            }
            value = angle ?? 0;
            break;
        }
        default:
            break;
    }

    return isNaN(value) ? 0 : value;
};
